/**
 * Shared option lists for Analysis Builder (Global Settings + Section config).
 * Used by GlobalReportSettings, ReportSectionCard, and generatePrompt.
 */

export interface ReportOption {
  value: string;
  label: string;
}

// Sentinel option shared by every list below so the "unset, let the AI
// decide" value is consistent (see isMeaningfulSelection in generatePrompt.ts).
export const LET_REBOOT_DECIDE_OPTION: ReportOption = {
  value: "let_reboot_decide",
  label: "Let Reboot decide",
};

export const ANALYSIS_TONE_OPTIONS: ReportOption[] = [
  { value: "technical-scientific", label: "Technical / Scientific" },
  { value: "coach-friendly", label: "Coach-Friendly" },
  { value: "executive-summary", label: "Executive Summary" },
  { value: "player-focused", label: "Player-Focused" },
  { value: "research-academic", label: "Research / Academic" },
  LET_REBOOT_DECIDE_OPTION,
];

export const TARGET_AUDIENCE_OPTIONS: ReportOption[] = [
  { value: "pitcher-athlete", label: "Pitcher / Athlete" },
  { value: "pitching-coach", label: "Pitching Coach" },
  { value: "athletic-trainer-pt", label: "Athletic Trainer / PT" },
  { value: "data-analyst-scientist", label: "Data Analyst / Scientist" },
  { value: "front-office-gm", label: "Front Office / GM" },
  { value: "parent-guardian", label: "Parent / Guardian" },
  LET_REBOOT_DECIDE_OPTION,
];

export const OUTPUT_FORMAT_OPTIONS: ReportOption[] = [
  { value: "html-report", label: "HTML Report" },
  LET_REBOOT_DECIDE_OPTION,
];

export const RECOMMENDATIONS_OPTIONS: ReportOption[] = [
  { value: "yes-actionable", label: "Yes, include actionable recommendations" },
  { value: "no-descriptive", label: "No, descriptive analysis only" },
  { value: "brief-only", label: "Brief recommendations only" },
  LET_REBOOT_DECIDE_OPTION,
];

export const METRICS_FOCUS_OPTIONS: ReportOption[] = [
  { value: "range_of_motion", label: "Range of Motion" },
  { value: "alignment_positioning", label: "Alignment / Positioning" },
  { value: "timing_sequencing", label: "Timing / Sequencing" },
  { value: "velocity_speed", label: "Velocity / Speed" },
  { value: "angular_velocity", label: "Angular Velocity" },
  { value: "force_load", label: "Force / Load" },
  { value: "torque_moments", label: "Torque / Moments" },
  { value: "efficiency_metrics", label: "Efficiency Metrics" },
  { value: "consistency_variability", label: "Consistency / Variability" },
  LET_REBOOT_DECIDE_OPTION,
];

// Categories shared by both baseball movement types.
const SHARED_BASEBALL_METRICS_FOCUS_OPTIONS: ReportOption[] = [
  { value: "kinematic_sequence", label: "Kinematic Sequence" },
  { value: "momentum_magnitude", label: "Momentum Magnitude" },
  { value: "rotation_plane_alignment", label: "Rotation Plane Alignment" },
];

/**
 * Metrics & Focus options scoped to a specific movement type's slug (see
 * `org.details.movementTypes[].slug`). Movement types not listed here fall
 * back to the generic METRICS_FOCUS_OPTIONS list via getMetricsFocusOptions.
 */
export const METRICS_FOCUS_OPTIONS_BY_MOVEMENT_SLUG: Record<
  string,
  ReportOption[]
> = {
  "baseball-pitching": [
    ...SHARED_BASEBALL_METRICS_FOCUS_OPTIONS,
    { value: "lateral_balance", label: "Lateral Balance" },
    { value: "joint_range_of_motion", label: "Joint Range of Motion" },
    LET_REBOOT_DECIDE_OPTION,
  ],
  "baseball-hitting": [
    ...SHARED_BASEBALL_METRICS_FOCUS_OPTIONS,
    { value: "bat_speed_attack_angle", label: "Bat Speed & Attack Angle" },
    {
      value: "swing_plane_path_consistency",
      label: "Swing Plane & Path Consistency",
    },
    { value: "bat_body_mechanics", label: "Bat-Body Mechanics" },
    { value: "rotational_kinetics", label: "Rotational Kinetics" },
    {
      value: "body_axis_trunk_orientation",
      label: "Body Axis & Trunk Orientation",
    },
    {
      value: "rotational_velocity_acceleration",
      label: "Rotational Velocity & Acceleration",
    },
    { value: "stride_stance", label: "Stride & Stance" },
    { value: "weight_transfer", label: "Weight Transfer" },
    {
      value: "segment_rotation_key_events",
      label: "Segment Rotation at Key Events",
    },
    { value: "batted_ball_outcomes", label: "Batted Ball Outcomes" },
    { value: "pitch_context", label: "Pitch Context" },
    LET_REBOOT_DECIDE_OPTION,
  ],
};

export function getMetricsFocusOptions(
  movementTypeSlug?: string,
): ReportOption[] {
  if (
    movementTypeSlug &&
    METRICS_FOCUS_OPTIONS_BY_MOVEMENT_SLUG[movementTypeSlug]
  ) {
    return METRICS_FOCUS_OPTIONS_BY_MOVEMENT_SLUG[movementTypeSlug];
  }
  return METRICS_FOCUS_OPTIONS;
}

export const ANALYSIS_STRUCTURE_OPTIONS: ReportOption[] = [
  { value: "trends_over_time", label: "Trends over time" },
  { value: "compare_dates_sessions", label: "Compare across dates/sessions" },
  { value: "compare_population_norms", label: "Compare to population norms" },
  { value: "find_correlations", label: "Find correlations between metrics" },
  { value: "identify_outliers", label: "Identify outliers or anomalies" },
  { value: "assess_consistency", label: "Assess consistency/variability" },
  { value: "track_improvement", label: "Track improvement/regression" },
  { value: "injury_risk", label: "Injury risk indicators" },
  LET_REBOOT_DECIDE_OPTION,
];

export const VISUALIZATION_OPTIONS: ReportOption[] = [
  { value: "line_chart", label: "Line Chart (trends)" },
  { value: "bar_chart", label: "Bar Chart (comparisons)" },
  { value: "scatter_plot", label: "Scatter Plot (relationships)" },
  { value: "heatmap", label: "Heatmap (patterns)" },
  { value: "data_table", label: "Data Table" },
  { value: "box_plot", label: "Box Plot (distributions)" },
  { value: "radar_chart", label: "Radar Chart (profiles)" },
  LET_REBOOT_DECIDE_OPTION,
];

export function optionsToLabelMap(
  options: ReportOption[],
): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

// Combines the generic list with every movement-specific list so a
// section's metricsFocus value can be resolved to a label regardless of
// which movement type was selected when it was chosen.
export const ALL_METRICS_FOCUS_OPTIONS: ReportOption[] = [
  ...METRICS_FOCUS_OPTIONS,
  ...Object.values(METRICS_FOCUS_OPTIONS_BY_MOVEMENT_SLUG).flat(),
];
